import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { auth } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';

const API = process.env.REACT_APP_API_URL || '';

const STEPS = ['Agence', 'Compte', 'Identité', 'Lancement'];

const ACCENT_PRESETS = [
  '#C8FF00', '#FF1F6A', '#00C8FF', '#FF6B00',
  '#A855F7', '#10B981', '#F59E0B', '#EF4444',
];

const s = {
  page:    { minHeight: '100vh', background: '#080808', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: '-apple-system, Arial, sans-serif' },
  wrap:    { width: '100%', maxWidth: 960, display: 'flex', gap: 32, alignItems: 'flex-start' },
  left:    { flex: '0 0 420px', background: '#111', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', padding: '36px 32px', position: 'sticky', top: 24 },
  right:   { flex: 1, background: '#111', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', padding: '36px 32px', minHeight: 480 },
  heading: { margin: '0 0 4px', fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' },
  sub:     { margin: '0 0 32px', fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 },
  label:   { display: 'block', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 },
  input:   { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '12px 14px', color: '#fff', fontSize: 14, outline: 'none', transition: 'border 0.2s' },
  field:   { marginBottom: 20 },
  btn:     (accent) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '14px 24px', background: accent, border: 'none', borderRadius: 12, color: '#080808', fontWeight: 900, fontSize: 15, cursor: 'pointer', transition: 'opacity 0.15s', letterSpacing: '-0.01em' }),
  back:    { background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 24px', color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%', marginBottom: 10 },
  err:     { background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 10, padding: '10px 14px', color: '#f87171', fontSize: 13, marginBottom: 20 },
};

function ProgressBar({ step, accent }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
      {STEPS.map((label, i) => (
        <div key={i} style={{ flex: 1 }}>
          <div style={{ height: 3, borderRadius: 4, background: i <= step ? accent : 'rgba(255,255,255,0.08)', marginBottom: 6, transition: 'background 0.3s' }} />
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: i === step ? '#fff' : 'rgba(255,255,255,0.25)' }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

function DashboardPreview({ agency }) {
  const accent = agency.accentColor || '#C8FF00';
  return (
    <div style={{ background: '#0a0a0a', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', userSelect: 'none' }}>
      {/* Topbar */}
      <div style={{ background: '#111', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 24, height: 24, borderRadius: 6, background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#080808' }}>
            {(agency.name || 'A').charAt(0).toUpperCase()}
          </div>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' }}>
            {agency.name || 'Mon Agence'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['Clients','Sites','Projets','Factures'].map(t => (
            <div key={t} style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.04)' }}>{t}</div>
          ))}
        </div>
      </div>
      {/* Body */}
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 12 }}>
          {['0 Clients','0 Sites','0 Projets','0 Factures'].map((k, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '10px 12px', border: `1px solid ${i === 0 ? accent + '33' : 'rgba(255,255,255,0.06)'}` }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: i === 0 ? accent : '#fff', letterSpacing: '-0.03em' }}>{k.split(' ')[0]}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{k.split(' ')[1]}</div>
            </div>
          ))}
        </div>
        {agency.tagline && (
          <div style={{ fontSize: 10, color: accent, fontStyle: 'italic', opacity: 0.8 }}>"{agency.tagline}"</div>
        )}
      </div>
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm] = useState({
    agencyName: '', city: '', tagline: '',
    ownerName: '', email: '', password: '', confirmPassword: '',
    accentColor: '#C8FF00',
  });
  const passwordRef = useRef(null);

  const accent = form.accentColor || '#C8FF00';

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); setError(''); }

  function validateStep() {
    if (step === 0) {
      if (!form.agencyName.trim()) return 'Le nom de l\'agence est requis.';
    }
    if (step === 1) {
      if (!form.ownerName.trim()) return 'Votre nom est requis.';
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Email invalide.';
      if (form.password.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères.';
      if (form.password !== form.confirmPassword) return 'Les mots de passe ne correspondent pas.';
    }
    return null;
  }

  function nextStep() {
    const err = validateStep();
    if (err) { setError(err); return; }
    setStep(s => s + 1);
  }

  async function submit() {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/onboarding/register`, {
        agencyName:  form.agencyName.trim(),
        ownerName:   form.ownerName.trim(),
        email:       form.email.trim().toLowerCase(),
        password:    form.password,
        city:        form.city.trim(),
        tagline:     form.tagline.trim(),
        accentColor: form.accentColor,
      });

      await signInWithCustomToken(auth, data.customToken);

      setStep(3);
      const agencyUrl = process.env.REACT_APP_AGENCY_DASHBOARD_URL || 'http://localhost:3001';
      setTimeout(() => { window.location.href = agencyUrl; }, 2800);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Erreur inattendue.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const previewAgency = {
    name:        form.agencyName || 'Mon Agence',
    tagline:     form.tagline,
    accentColor: accent,
  };

  return (
    <div style={s.page}>
      <div style={s.wrap}>

        {/* ── PANNEAU GAUCHE ── */}
        <div style={s.left}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 8 }}>Propulsé par</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: '-0.04em' }}>
              U<span style={{ color: accent, transition: 'color 0.3s' }}>creative</span>
            </div>
          </div>

          <ProgressBar step={step} accent={accent} />

          <div style={{ marginBottom: 28 }}>
            <DashboardPreview agency={previewAgency} />
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 20 }}>
            <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Inclus dans l'essai</p>
            {['✓ Dashboard complet', '✓ Gestion clients & projets', '✓ Sites vitrines', '✓ Facturation', '✓ IA intégrée', '✓ 14 jours gratuits'].map((f, i) => (
              <div key={i} style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: accent }}>{f.split(' ')[0]}</span>
                <span>{f.slice(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── PANNEAU DROIT ── */}
        <div style={s.right}>
          {step === 0 && <StepAgence form={form} set={set} error={error} accent={accent} onNext={nextStep} />}
          {step === 1 && <StepCompte form={form} set={set} error={error} accent={accent} onNext={nextStep} onBack={() => setStep(0)} passwordRef={passwordRef} />}
          {step === 2 && <StepIdentite form={form} set={set} error={error} accent={accent} onNext={submit} onBack={() => setStep(1)} loading={loading} />}
          {step === 3 && <StepLancement accent={accent} agencyName={form.agencyName} ownerName={form.ownerName} />}
        </div>

      </div>
    </div>
  );
}

// ── ÉTAPE 1 — Agence ──────────────────────────────────────────────────────────
function StepAgence({ form, set, error, accent, onNext }) {
  return (
    <>
      <h2 style={s.heading}>Créez votre agence</h2>
      <p style={s.sub}>Quelques informations pour configurer votre espace de travail.</p>

      {error && <div style={s.err}>{error}</div>}

      <div style={s.field}>
        <label style={s.label}>Nom de l'agence *</label>
        <input style={s.input} value={form.agencyName} onChange={e => set('agencyName', e.target.value)}
          placeholder="Ex: Studio Nova" autoFocus onKeyDown={e => e.key === 'Enter' && onNext()} />
      </div>
      <div style={s.field}>
        <label style={s.label}>Ville</label>
        <input style={s.input} value={form.city} onChange={e => set('city', e.target.value)}
          placeholder="Ex: Montréal" onKeyDown={e => e.key === 'Enter' && onNext()} />
      </div>
      <div style={s.field}>
        <label style={s.label}>Slogan (optionnel)</label>
        <input style={s.input} value={form.tagline} onChange={e => set('tagline', e.target.value)}
          placeholder="Ex: Design qui convertit" onKeyDown={e => e.key === 'Enter' && onNext()} />
      </div>

      <button style={s.btn(accent)} onClick={onNext}>Continuer →</button>
    </>
  );
}

// ── ÉTAPE 2 — Compte ──────────────────────────────────────────────────────────
function StepCompte({ form, set, error, accent, onNext, onBack }) {
  return (
    <>
      <h2 style={s.heading}>Votre compte admin</h2>
      <p style={s.sub}>Ces identifiants vous serviront à vous connecter au dashboard.</p>

      {error && <div style={s.err}>{error}</div>}

      <div style={s.field}>
        <label style={s.label}>Votre nom *</label>
        <input style={s.input} value={form.ownerName} onChange={e => set('ownerName', e.target.value)}
          placeholder="Jean Tremblay" autoFocus />
      </div>
      <div style={s.field}>
        <label style={s.label}>Email *</label>
        <input style={s.input} type="email" value={form.email} onChange={e => set('email', e.target.value)}
          placeholder="jean@monagence.ca" />
      </div>
      <div style={s.field}>
        <label style={s.label}>Mot de passe *</label>
        <input style={s.input} type="password" value={form.password} onChange={e => set('password', e.target.value)}
          placeholder="8 caractères minimum" />
      </div>
      <div style={s.field}>
        <label style={s.label}>Confirmer le mot de passe *</label>
        <input style={s.input} type="password" value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)}
          placeholder="Répétez le mot de passe" onKeyDown={e => e.key === 'Enter' && onNext()} />
      </div>

      <button style={s.btn(accent)} onClick={onNext}>Continuer →</button>
      <button style={s.back} onClick={onBack}>← Retour</button>
    </>
  );
}

// ── ÉTAPE 3 — Identité visuelle ───────────────────────────────────────────────
function StepIdentite({ form, set, error, accent, onNext, onBack, loading }) {
  return (
    <>
      <h2 style={s.heading}>Identité visuelle</h2>
      <p style={s.sub}>Choisissez la couleur d'accent de votre dashboard.</p>

      {error && <div style={s.err}>{error}</div>}

      <div style={s.field}>
        <label style={s.label}>Couleur d'accent</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          {ACCENT_PRESETS.map(c => (
            <button key={c} onClick={() => set('accentColor', c)} style={{
              width: 36, height: 36, borderRadius: 8, background: c, border: 'none', cursor: 'pointer',
              outline: form.accentColor === c ? `2.5px solid #fff` : '2.5px solid transparent',
              outlineOffset: 2, boxShadow: form.accentColor === c ? `0 0 12px ${c}88` : 'none',
              transition: 'all 0.15s',
            }} />
          ))}
          <input type="color" value={form.accentColor} onChange={e => set('accentColor', e.target.value)}
            style={{ width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', padding: 0 }} title="Couleur personnalisée" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, background: accent, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace' }}>{accent}</span>
        </div>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '16px 18px', marginBottom: 24, border: '1px solid rgba(255,255,255,0.06)' }}>
        <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Résumé</p>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 2 }}>
          <div><span style={{ color: 'rgba(255,255,255,0.3)' }}>Agence :</span> {form.agencyName}</div>
          <div><span style={{ color: 'rgba(255,255,255,0.3)' }}>Admin  :</span> {form.ownerName}</div>
          <div><span style={{ color: 'rgba(255,255,255,0.3)' }}>Email  :</span> {form.email}</div>
          {form.city && <div><span style={{ color: 'rgba(255,255,255,0.3)' }}>Ville  :</span> {form.city}</div>}
        </div>
      </div>

      <button style={{ ...s.btn(accent), opacity: loading ? 0.6 : 1 }} onClick={onNext} disabled={loading}>
        {loading ? 'Création en cours…' : '🚀 Lancer mon agence'}
      </button>
      <button style={s.back} onClick={onBack} disabled={loading}>← Retour</button>
    </>
  );
}

// ── ÉTAPE 4 — Lancement ───────────────────────────────────────────────────────
function StepLancement({ accent, agencyName, ownerName }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ fontSize: 64, marginBottom: 24, animation: 'none' }}>🎉</div>
      <h2 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' }}>
        Bienvenue, {ownerName} !
      </h2>
      <p style={{ margin: '0 0 32px', fontSize: 15, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
        <strong style={{ color: '#fff' }}>{agencyName}</strong> est maintenant active.<br />
        Redirection vers le dashboard…
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 260, margin: '0 auto' }}>
        {['Compte créé ✓', 'Agence configurée ✓', 'Email envoyé ✓', 'Connexion auto…'].map((line, i) => (
          <div key={i} style={{ fontSize: 13, color: i < 3 ? accent : 'rgba(255,255,255,0.4)', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: i < 3 ? accent : 'rgba(255,255,255,0.2)' }} />
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}
